import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';

const parseUserAgent = (ua = '') => {
  let browser = 'Unknown';
  let os = 'Unknown';
  let deviceType = 'desktop';

  if (/mobile/i?.test(ua)) deviceType = 'mobile';
  else if (/tablet|ipad/i?.test(ua)) deviceType = 'tablet';

  if (/chrome/i?.test(ua) && !/edge|opr/i?.test(ua)) browser = 'Chrome';
  else if (/firefox/i?.test(ua)) browser = 'Firefox';
  else if (/safari/i?.test(ua) && !/chrome/i?.test(ua)) browser = 'Safari';
  else if (/edge/i?.test(ua)) browser = 'Edge';
  else if (/opr|opera/i?.test(ua)) browser = 'Opera';

  if (/windows/i?.test(ua)) os = 'Windows';
  else if (/mac os/i?.test(ua)) os = 'macOS';
  else if (/linux/i?.test(ua)) os = 'Linux';
  else if (/android/i?.test(ua)) os = 'Android';
  else if (/ios|iphone|ipad/i?.test(ua)) os = 'iOS';

  return { browser, os, deviceType };
};

const recordLoginActivity = async ({ userId, email, organizationId, success, failureReason, sessionId }) => {
  try {
    const ua = navigator?.userAgent || '';
    const { browser, os, deviceType } = parseUserAgent(ua);
    await supabase?.from('login_activity')?.insert({
      user_id: userId || null,
      email,
      organization_id: organizationId || null,
      ip_address: null,
      user_agent: ua,
      device_type: deviceType,
      browser,
      os,
      success,
      failure_reason: failureReason || null,
      session_id: sessionId || null,
    });
  } catch (e) {
    console.warn('Login activity recording failed:', e);
  }
};

const createSessionRecord = async ({ userId, organizationId }) => {
  try {
    const ua = navigator?.userAgent || '';
    const { browser, os, deviceType } = parseUserAgent(ua);
    const sessionToken = `session_${Date.now()}_${Math.random()?.toString(36)?.substr(2, 9)}`;

    await supabase?.from('user_sessions')?.update({ is_current: false })?.eq('user_id', userId);

    await supabase?.from('user_sessions')?.insert({
      user_id: userId,
      organization_id: organizationId || null,
      session_token: sessionToken,
      device_type: deviceType,
      browser,
      os,
      user_agent: ua,
      is_current: true,
      last_active_at: new Date()?.toISOString(),
    });

    localStorage.setItem('sj_session_token', sessionToken);
  } catch (e) {
    console.warn('Session record creation failed:', e);
  }
};

const RequestAccessModal = ({ onClose }) => {
  const { t } = useLanguage();
  const [form, setForm] = useState({ email: '', companyName: '', numberOfUsers: '', phoneNumber: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e?.target?.name]: e?.target?.value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      const { data, error: fnError } = await supabase?.functions?.invoke('send-access-request', {
        body: {
          email: form?.email,
          companyName: form?.companyName,
          numberOfUsers: form?.numberOfUsers,
          phoneNumber: form?.phoneNumber,
        },
      });
      if (fnError) throw new Error(fnError.message || 'Failed to send request');
      setSubmitted(true);
    } catch (err) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyle = {
    background: '#111111',
    border: '1px solid #444444',
    color: '#FFFFFF',
    outline: 'none',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 sm:px-6" style={{ background: 'rgba(0,0,0,0.75)' }}>
      <div className="w-full max-w-md rounded-2xl p-6 sm:p-8 relative card-3d-dark" style={{ border: '1px solid rgba(255,255,255,0.08)', maxHeight: '90vh', overflowY: 'auto' }}>
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1 rounded-lg transition-colors"
          style={{ color: '#999999' }}
          onMouseEnter={(e) => e.currentTarget.style.color = '#FFFFFF'}
          onMouseLeave={(e) => e.currentTarget.style.color = '#999999'}
        >
          <Icon name="X" size={20} />
        </button>

        {submitted ? (
          <div className="text-center py-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ background: 'rgba(204,0,0,0.15)', border: '1px solid #CC0000' }}>
              <Icon name="CheckCircle" size={32} color="#CC0000" />
            </div>
            <h3 className="text-xl font-bold mb-2" style={{ color: '#FFFFFF' }}>{t('login_requestSent')}</h3>
            <p className="text-sm mb-6" style={{ color: '#999999' }}>
              {t('login_requestSentDesc')}
            </p>
            <button
              onClick={onClose}
              className="px-6 py-2.5 rounded-xl font-semibold text-sm uppercase tracking-wider transition-all"
              style={{ background: '#CC0000', color: '#FFFFFF' }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#FF0000'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#CC0000'}
            >
              {t('login_close')}
            </button>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <h3 className="text-xl font-bold mb-1" style={{ color: '#FFFFFF' }}>{t('login_requestTitle')}</h3>
              <p className="text-sm" style={{ color: '#999999' }}>{t('login_requestSubtitle')}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="rounded-lg p-3 flex items-center gap-2" style={{ background: 'rgba(204,0,0,0.15)', border: '1px solid #CC0000' }}>
                  <Icon name="AlertCircle" size={16} color="#FF4444" />
                  <p className="text-sm" style={{ color: '#FF4444' }}>{error}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: '#CCCCCC' }}>{t('login_emailAddress')}</label>
                <input
                  type="email"
                  name="email"
                  value={form?.email}
                  onChange={handleChange}
                  placeholder={t('login_emailPlaceholder')}
                  required
                  className="w-full px-4 py-2.5 rounded-lg text-sm transition-colors"
                  style={inputStyle}
                  onFocus={(e) => { e.target.style.borderColor = '#CC0000'; e.target.style.boxShadow = '0 0 0 2px rgba(204,0,0,0.25)'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#444444'; e.target.style.boxShadow = 'none'; }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: '#CCCCCC' }}>{t('login_companyName')}</label>
                <input
                  type="text"
                  name="companyName"
                  value={form?.companyName}
                  onChange={handleChange}
                  placeholder={t('login_companyPlaceholder')}
                  required
                  className="w-full px-4 py-2.5 rounded-lg text-sm transition-colors"
                  style={inputStyle}
                  onFocus={(e) => { e.target.style.borderColor = '#CC0000'; e.target.style.boxShadow = '0 0 0 2px rgba(204,0,0,0.25)'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#444444'; e.target.style.boxShadow = 'none'; }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: '#CCCCCC' }}>{t('login_numberOfUsers')}</label>
                <input
                  type="number"
                  inputMode="numeric"
                  name="numberOfUsers"
                  value={form?.numberOfUsers}
                  onChange={handleChange}
                  placeholder={t('login_numberOfUsersPlaceholder')}
                  min="1"
                  required
                  className="w-full px-4 py-2.5 rounded-lg text-sm transition-colors"
                  style={inputStyle}
                  onFocus={(e) => { e.target.style.borderColor = '#CC0000'; e.target.style.boxShadow = '0 0 0 2px rgba(204,0,0,0.25)'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#444444'; e.target.style.boxShadow = 'none'; }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: '#CCCCCC' }}>{t('login_phoneNumber')}</label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={form?.phoneNumber}
                  onChange={handleChange}
                  placeholder={t('login_phonePlaceholder')}
                  required
                  className="w-full px-4 py-2.5 rounded-lg text-sm transition-colors"
                  style={inputStyle}
                  onFocus={(e) => { e.target.style.borderColor = '#CC0000'; e.target.style.boxShadow = '0 0 0 2px rgba(204,0,0,0.25)'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#444444'; e.target.style.boxShadow = 'none'; }}
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex items-center justify-center gap-2 font-bold py-3 px-4 rounded-xl transition-all duration-200 mt-2 uppercase tracking-wider text-sm btn-3d-red"
                style={{
                  background: isSubmitting ? '#991111' : 'linear-gradient(180deg, #E00000 0%, #CC0000 50%, #AA0000 100%)',
                  color: '#FFFFFF',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                }}
                onMouseEnter={(e) => { if (!isSubmitting) { e.currentTarget.style.background = 'linear-gradient(180deg, #FF2020 0%, #E00000 50%, #CC0000 100%)'; }}}
                onMouseLeave={(e) => { if (!isSubmitting) { e.currentTarget.style.background = 'linear-gradient(180deg, #E00000 0%, #CC0000 50%, #AA0000 100%)'; }}}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>{t('login_sending')}</span>
                  </>
                ) : (
                  <>
                    <Icon name="Send" size={16} color="white" />
                    <span>{t('login_submitRequest')}</span>
                  </>
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

const Login = () => {
  const navigate = useNavigate();
  const { signIn, user, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);

  // Detect trial=started param set by Stripe success redirect
  const isTrialStarted = searchParams?.get('trial') === 'started';

  useEffect(() => {
    if (!loading && user) {
      navigate('/my-dashboard', { replace: true });
    }
  }, [user, loading, navigate]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const { data, error: signInError } = await signIn(email, password);
      if (signInError) {
        const msg = (signInError?.message || '')?.toLowerCase();

        // Network / connectivity failures
        if (
          msg?.includes('network') ||
          msg?.includes('failed to fetch') ||
          msg?.includes('fetch') ||
          msg?.includes('connection')
        ) {
          setError('Unable to connect to the server. Please check your internet connection and try again.');
        }
        // Account disabled / banned
        else if (
          msg?.includes('user banned') ||
          msg?.includes('account disabled') ||
          msg?.includes('account has been disabled') ||
          msg?.includes('user_banned')
        ) {
          setError('Your account has been disabled. Please contact your administrator for assistance.');
        }
        // Email not confirmed
        else if (
          msg?.includes('email not confirmed') ||
          msg?.includes('email_not_confirmed')
        ) {
          setError('Your email address has not been confirmed. Please check your inbox for a confirmation link.');
        }
        // Too many requests / rate limited
        else if (
          msg?.includes('too many requests') ||
          msg?.includes('rate limit') ||
          msg?.includes('over_request_rate_limit') ||
          msg?.includes('email rate limit')
        ) {
          setError('Too many login attempts. Please wait a few minutes before trying again.');
        }
        // Invalid credentials (wrong email or password)
        else if (
          msg?.includes('invalid login credentials') ||
          msg?.includes('invalid credentials') ||
          msg?.includes('wrong password') ||
          msg?.includes('user not found') ||
          msg?.includes('no user found') ||
          msg?.includes('invalid email or password')
        ) {
          setError('Incorrect email or password. Please double-check your credentials and try again.');
        }
        // Expired / invalid token or session
        else if (
          msg?.includes('token expired') ||
          msg?.includes('invalid token') ||
          msg?.includes('jwt expired') ||
          msg?.includes('session expired')
        ) {
          setError('Your session has expired. Please sign in again.');
        }
        // Fallback: show the raw message if it's meaningful, otherwise generic
        else {
          const rawMsg = signInError?.message || '';
          setError(rawMsg && rawMsg?.length < 200 ? rawMsg : 'Sign in failed. Please verify your credentials and try again.');
        }

        await recordLoginActivity({
          email,
          success: false,
          failureReason: signInError?.message || 'Sign in failed',
        });
      } else {
        const userId = data?.user?.id;
        let orgId = null;
        let systemRole = null;
        let isFirstLogin = false;

        if (userId) {
          const { data: profile } = await supabase
            ?.from('user_profiles')
            ?.select('organization_id, system_role, first_login_checklist_dismissed, first_login_checklist_completed, onboarding_completed, email')
            ?.eq('id', userId)
            ?.single();
          orgId = profile?.organization_id;
          systemRole = profile?.system_role;

          // If system_role or org_id is missing from user_profiles, derive from org_members
          // (handles new users whose user_profiles row hasn't been fully populated yet)
          if (!systemRole || !orgId) {
            let memberRow = null;
            const { data: memberByUserId } = await supabase
              ?.from('org_members')
              ?.select('role, organization_id')
              ?.eq('user_id', userId)
              ?.eq('is_active', true)
              ?.single() || {};

            if (memberByUserId) {
              memberRow = memberByUserId;
            } else if (profile?.email || email) {
              const lookupEmail = profile?.email || email;
              let emailQuery = supabase
                ?.from('org_members')
                ?.select('role, organization_id')
                ?.eq('email', lookupEmail)
                ?.eq('is_active', true)
                ?.order('created_at', { ascending: false })
                ?.limit(1);
              if (orgId) emailQuery = emailQuery?.eq('organization_id', orgId);
              const { data: memberByEmail } = await emailQuery?.single() || {};
              if (memberByEmail) memberRow = memberByEmail;
            }

            if (memberRow) {
              if (!orgId) orgId = memberRow?.organization_id;
              if (!systemRole) {
                systemRole = (memberRow?.role === 'owner' || memberRow?.role === 'admin')
                  ? 'org_admin' :'org_member';
              }
            }
          }

          // Determine if all checklist items are completed
          const CHECKLIST_STEP_IDS = [
            'verify_org_profile', 'verify_branding', 'verify_tax_rate',
            'configure_roles', 'invite_team', 'add_personnel',
            'review_demo_data', 'review_ticket_workflow', 'reset_or_keep_demo',
          ];
          let allChecklistDone = false;
          if (profile?.first_login_checklist_completed) {
            try {
              const completedIds = JSON.parse(profile?.first_login_checklist_completed);
              allChecklistDone = Array.isArray(completedIds) &&
                CHECKLIST_STEP_IDS?.every(id => completedIds?.includes(id));
            } catch (_) {}
          }

          // Redirect org admins to checklist if not all items are done
          // (isTrialStarted always forces checklist for new trial signups)
          isFirstLogin = isTrialStarted || !allChecklistDone;

          // If this user hasn't completed the checklist, check if another org member already has.
          // If so, onboarding is already done for the org — skip the checklist for this user.
          if (isFirstLogin && systemRole === 'org_admin' && orgId && !isTrialStarted) {
            try {
              const { data: orgProfiles } = await supabase
                ?.from('user_profiles')
                ?.select('first_login_checklist_completed')
                ?.eq('organization_id', orgId)
                ?.neq('id', userId);

              if (orgProfiles && orgProfiles?.length > 0) {
                const orgOnboardingDone = orgProfiles?.some(p => {
                  if (!p?.first_login_checklist_completed) return false;
                  try {
                    const ids = JSON.parse(p?.first_login_checklist_completed);
                    return Array.isArray(ids) && CHECKLIST_STEP_IDS?.every(id => ids?.includes(id));
                  } catch (_) {
                    return false;
                  }
                });
                if (orgOnboardingDone) isFirstLogin = false;
              }
            } catch (_) {}
          }
        }

        await recordLoginActivity({ userId, email, organizationId: orgId, success: true, sessionId: data?.session?.access_token?.slice(-12) });
        if (userId) await createSessionRecord({ userId, organizationId: orgId });

        navigate('/my-dashboard', { replace: true });
      }
    } catch (err) {
      setError(t('login_unexpectedError'));
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#111111' }}>
        <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: '#CC0000' }}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 py-8 sm:py-0" style={{ 
      background: 'radial-gradient(ellipse at 30% 20%, #2a0000 0%, #111111 40%, #0a0a0a 70%, #111111 100%)',
    }}>
      {/* Yellow accent stripe at top */}
      <div className="fixed top-0 left-0 right-0 h-1" style={{ background: 'linear-gradient(90deg, #AA0000, #FFD700, #CC0000)', boxShadow: '0 2px 12px rgba(255,215,0,0.4)' }}></div>

      {/* Ambient depth layers */}
      <div className="fixed inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 60% 40% at 50% 0%, rgba(204,0,0,0.08) 0%, transparent 70%)' }}></div>
      <div className="fixed inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 40% 60% at 80% 80%, rgba(255,215,0,0.03) 0%, transparent 60%)' }}></div>

      {showRequestModal && <RequestAccessModal onClose={() => setShowRequestModal(false)} />}

      <div className="w-full max-w-md" style={{ perspective: '1000px' }}>
        {/* Logo / Brand */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="flex items-center justify-center mb-3 sm:mb-4">
            <img
              src="/assets/images/ServiceJockeyLogo_Signature-1782343696238.png"
              alt="Service Jockey Logo"
              className="h-20 sm:h-24 w-auto object-contain"
            />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-widest uppercase" style={{ 
            color: '#FFFFFF', 
            letterSpacing: '0.15em',
            textShadow: '0 2px 4px rgba(0,0,0,0.5), 0 4px 12px rgba(0,0,0,0.3)',
          }}>
            SERVICE<span style={{ color: '#FFD700', textShadow: '0 2px 8px rgba(255,215,0,0.4), 0 4px 16px rgba(255,215,0,0.2)' }}>JOCKEY</span>
          </h1>
          <div className="flex items-center justify-center gap-2 mt-2">
            <div className="h-px w-8 sm:w-12" style={{ background: 'linear-gradient(90deg, transparent, #CC0000)', boxShadow: '0 0 6px rgba(204,0,0,0.5)' }}></div>
            <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#FFD700', textShadow: '0 1px 4px rgba(255,215,0,0.3)' }}>Field Service Management</p>
            <div className="h-px w-8 sm:w-12" style={{ background: 'linear-gradient(90deg, #CC0000, transparent)', boxShadow: '0 0 6px rgba(204,0,0,0.5)' }}></div>
          </div>
        </div>

        {/* Card */}
        <div 
          className="rounded-2xl p-5 sm:p-8 card-3d-dark"
          style={{ 
            background: 'linear-gradient(160deg, #222222 0%, #1C1C1C 40%, #161616 100%)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderTop: '1px solid rgba(255,255,255,0.12)',
          }}
        >
          <div className="mb-5 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-semibold mb-1" style={{ color: '#FFFFFF', textShadow: '0 1px 3px rgba(0,0,0,0.4)' }}>{t('login_signIn')}</h2>
            <p className="text-sm" style={{ color: '#999999' }}>{t('login_enterCredentials')}</p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
            {error && (
              <div className="rounded-lg p-3 flex items-center gap-2" style={{ background: 'rgba(204,0,0,0.15)', border: '1px solid rgba(204,0,0,0.4)', boxShadow: '0 2px 8px rgba(204,0,0,0.2) inset' }}>
                <Icon name="AlertCircle" size={16} color="#FF4444" />
                <p className="text-sm" style={{ color: '#FF4444' }}>{error}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#CCCCCC' }}>{t('login_emailAddress')}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e?.target?.value)}
                placeholder={t('login_emailPlaceholder')}
                required
                className="w-full px-3 sm:px-4 py-2.5 rounded-lg text-sm transition-all input-3d"
                style={{
                  background: 'linear-gradient(180deg, #0D0D0D 0%, #111111 100%)',
                  border: '1px solid #3A3A3A',
                  borderTop: '1px solid #2A2A2A',
                  color: '#FFFFFF',
                  outline: 'none',
                }}
                onFocus={(e) => { e.target.style.borderColor = '#CC0000'; e.target.style.boxShadow = '0 2px 6px rgba(0,0,0,0.4) inset, 0 0 0 2px rgba(204,0,0,0.25), 0 0 16px rgba(204,0,0,0.1)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#3A3A3A'; e.target.style.boxShadow = '0 2px 6px rgba(0,0,0,0.4) inset, 0 1px 0 rgba(255,255,255,0.04)'; }}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm font-medium" style={{ color: '#CCCCCC' }}>{t('login_password')}</label>
                <Link
                  to="/forgot-password"
                  className="text-xs transition-colors"
                  style={{ color: '#CC0000' }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#FF4444'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#CC0000'}
                >
                  {t('login_forgotPassword')}
                </Link>
              </div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e?.target?.value)}
                placeholder="••••••••"
                required
                className="w-full px-3 sm:px-4 py-2.5 rounded-lg text-sm transition-all input-3d"
                style={{
                  background: 'linear-gradient(180deg, #0D0D0D 0%, #111111 100%)',
                  border: '1px solid #3A3A3A',
                  borderTop: '1px solid #2A2A2A',
                  color: '#FFFFFF',
                  outline: 'none',
                }}
                onFocus={(e) => { e.target.style.borderColor = '#CC0000'; e.target.style.boxShadow = '0 2px 6px rgba(0,0,0,0.4) inset, 0 0 0 2px rgba(204,0,0,0.25), 0 0 16px rgba(204,0,0,0.1)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#3A3A3A'; e.target.style.boxShadow = '0 2px 6px rgba(0,0,0,0.4) inset, 0 1px 0 rgba(255,255,255,0.04)'; }}
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2 font-bold py-3 px-4 rounded-xl transition-all duration-200 mt-2 uppercase tracking-wider text-sm btn-3d-red"
              style={{
                background: isLoading ? 'linear-gradient(180deg, #881111 0%, #771111 100%)' : 'linear-gradient(180deg, #E00000 0%, #CC0000 50%, #AA0000 100%)',
                color: '#FFFFFF',
                cursor: isLoading ? 'not-allowed' : 'pointer',
              }}
              onMouseEnter={(e) => { if (!isLoading) { e.currentTarget.style.background = 'linear-gradient(180deg, #FF2020 0%, #E00000 50%, #CC0000 100%)'; }}}
              onMouseLeave={(e) => { if (!isLoading) { e.currentTarget.style.background = 'linear-gradient(180deg, #E00000 0%, #CC0000 50%, #AA0000 100%)'; }}}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>{t('login_signingIn')}</span>
                </>
              ) : (
                <>
                  <Icon name="LogIn" size={18} color="white" />
                  <span>{t('login_signInBtn')}</span>
                </>
              )}
            </button>
          </form>

          {/* Request Access Button */}
          <div className="mt-3 sm:mt-4">
            <button
              type="button"
              onClick={() => setShowRequestModal(true)}
              className="w-full flex items-center justify-center gap-2 font-semibold py-2.5 px-4 rounded-xl transition-all duration-200 uppercase tracking-wider text-sm btn-3d"
              style={{
                background: 'linear-gradient(180deg, rgba(255,215,0,0.06) 0%, transparent 100%)',
                color: '#FFD700',
                border: '1px solid rgba(255,215,0,0.35)',
                borderTop: '1px solid rgba(255,215,0,0.5)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'linear-gradient(180deg, rgba(255,215,0,0.12) 0%, rgba(255,215,0,0.04) 100%)'; e.currentTarget.style.borderColor = 'rgba(255,215,0,0.6)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'linear-gradient(180deg, rgba(255,215,0,0.06) 0%, transparent 100%)'; e.currentTarget.style.borderColor = 'rgba(255,215,0,0.35)'; }}
            >
              <Icon name="UserPlus" size={16} color="#FFD700" />
              <span>{t('login_requestAccess')}</span>
            </button>
          </div>
        </div>

        {/* Trial message */}
        <div className="mt-4 text-center px-2">
          <p className="text-sm" style={{ color: '#CCCCCC' }}>
            <Link
              to="/paywall"
              style={{ color: '#FFD700' }}
              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
            >
              {t('login_trialCta')}
            </Link>
          </p>
        </div>

        {/* Footer */}
        <p className="text-center text-xs mt-4" style={{ color: '#555555' }}>
          {t('login_copyright', { year: new Date()?.getFullYear() })}
        </p>
      </div>
    </div>
  );
};

export default Login;
